// components/Inventar/InventarDetail.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showSuccess, showError, showForbidden } from "../../utils/toast.js";
import api from "../../constants/api";
import { smartBack, navigateToList } from "../../constants/nav";
import { invalidateListDomain } from "../../src/hooks/listQueryCache.js";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import { useAuth } from "../ui/AuthContext.jsx";
import AppButton from "../ui/AppButton.jsx";
import { detailPageHeaderNavClassName, detailPageHeaderNavActionsClassName } from "../../design/tokens.js";
import { ROLE_MITARBEITER, getRoleId, isPowerUser } from "../../constants/roles.js";
import InventarBlockInfo from "./InventarBlockInfo";
import InventarBlockFotos from "./InventarBlockFotos";
import InventarBlockHistorie from "./InventarBlockHistorie";
import { sanitizeFileUrl } from "../../utils/filePreview.js";

function pickInventarFotoUrl(row) {
  for (const candidate of [
    row?.secure_preview_url,
    row?.secure_download_url,
    row?.secure_url,
  ]) {
    const safe = sanitizeFileUrl(candidate || "");
    if (safe) return safe;
  }
  return "";
}
export default function InventarDetail() {
  const {
    id
  } = useParams();
  const artikelId = useMemo(() => Number.parseInt(id, 10), [id]);
  const navigate = useNavigate();
    const {
    user
  } = useAuth();
  const confirm = useConfirm();
  const roleId = getRoleId(user);
  const canWrite = isPowerUser(user) || roleId === ROLE_MITARBEITER;
  const canDelete = isPowerUser(user);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort?.();
    };
  }, []);
  useEffect(() => {
    const old = document.title;
    document.title = `Inventar #${artikelId} • WireTrack`;
    return () => {
      document.title = old;
    };
  }, [artikelId]);
  const load = useCallback(async () => {
    abortRef.current?.abort?.();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      setLoading(true);
      setLoadError("");
      const res = await api.get(`/inventar/${artikelId}`, {
        signal: ac.signal
      });
      if (!mountedRef.current) return;
      const data = res?.data?.data || res?.data || null;
      const fotos = Array.isArray(data?.fotos)
        ? data.fotos.map((f) => {
            const safeUrl = pickInventarFotoUrl(f);
            return safeUrl ? { ...f, url: safeUrl } : { ...f };
          })
        : [];
      setItem({
        ...data,
        fotos
      });
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;
      const s = e?.response?.status;
      if (s === 401) {
        showError("Bitte erneut einloggen.");
        navigate("/login", {
          replace: true
        });
        return;
      }
      if (s === 404) {
        showError("Inventar-Eintrag nicht gefunden.");
        navigate("/inventar", {
          replace: true
        });
        return;
      }
      console.error(e);
      const msg = e?.response?.data?.error || "Inventar-Eintrag konnte nicht geladen werden";
      setLoadError(msg);
      setItem(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [artikelId, navigate]);
  useEffect(() => {
    load();
  }, [load]);
  const handleEdit = () => navigate(`/inventar/${artikelId}/bearbeiten`);
  const handleDelete = async () => {
    if (!canDelete) {
      showForbidden("restricted_role", {
        level: "error"
      });
      return;
    }
    const {
      ok
    } = await confirm({
      title: "Artikel löschen",
      message: `«${item?.bezeichnung?.trim() || `Artikel #${artikelId}`}» wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      variant: "danger",
      confirmLabel: "Löschen",
    });
    if (!ok) return;
    try {
      await api.delete(`/inventar/${artikelId}`);
      showSuccess("In Papierkorb verschoben");
      navigateToList(navigate, "/inventar");
    } catch (e) {
      console.error(e);
      showError("Löschen fehlgeschlagen");
    }
  };
  const pageCls = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const cardCls = "rounded-2xl border app-frame";
  const skeletonCls = "rounded-2xl border app-frame";
  if (loading) {
    return <div className={pageCls}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`${skeletonCls} h-20 mb-6 animate-pulse`} />
          <div className="grid gap-4">
            <div className={`${skeletonCls} h-40 animate-pulse`} />
            <div className={`${skeletonCls} h-40 animate-pulse`} />
          </div>
        </div>
      </div>;
  }
  if (!item) {
    return <div className={pageCls}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`${cardCls} p-5`}>
            <div className="text-red-500">
              ❌ {loadError || "Inventar-Eintrag nicht gefunden."}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {loadError && <AppButton onClick={load} variant="secondary" size="sm" type="button">
                  Erneut versuchen
                </AppButton>}
              <AppButton type="button" onClick={() => navigateToList(navigate, "/inventar")} variant="secondary" size="sm">
                ← Zur Übersicht
              </AppButton>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className={pageCls}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        {/* Seiten-Header (gleich wie bei Projekten) */}
        <div className={`${cardCls} px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6`}>
          <div className="min-w-0">
            <h1 className={`text-2xl font-bold tracking-tight truncate ${"text-blue-100/90"}`}>
              {item?.bezeichnung || `Inventar #${artikelId}`}
            </h1>
            <div className={`mt-1 text-sm flex gap-2 flex-wrap ${"text-blue-200/80"}`}>
              <span>{item?.projekttyp || "–"} · {item?.kategorie || "–"}</span>
            </div>
          </div>

          <div className={`${detailPageHeaderNavActionsClassName} mt-3 sm:mt-0`}>
            <AppButton type="button" onClick={() => smartBack(navigate, ["/inventar", "/"])} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Zurück</AppButton>
            <AppButton type="button" onClick={() => navigateToList(navigate, "/inventar")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Inventarliste</AppButton>
            <AppButton type="button" onClick={() => navigate("/meine-aufgaben")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Meine Aufgaben</AppButton>
            <AppButton type="button" onClick={() => navigate("/")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Dashboard</AppButton>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <InventarBlockInfo
            item={item}
            canWrite={canWrite}
            canDelete={canDelete}
            onEdit={() => handleEdit()}
            onDelete={() => handleDelete()}
            onStatusSaved={() => load()}
          />
          <InventarBlockFotos artikelId={item.id} />
          <InventarBlockHistorie artikelId={item.id} />
        </div>

      </div>
    </div>;
}